/**
 * Phase 14.5 — QorBOM Docs · Try-It-Now sandbox widget.
 *
 * Extracted from the old single-file QorbomApiDocs.jsx so the new
 * 3-column shell can drop it into the Quickstart page without
 * dragging in the whole legacy file.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, ArrowRight, Loader2, Play } from "lucide-react";

import QorbomBrand from "../qorbom/QorbomBrand";

const API = process.env.REACT_APP_BACKEND_URL;


export default function TryItNow() {
  const [repoUrl,  setRepoUrl]  = useState("https://github.com/octocat/Hello-World");
  const [status,   setStatus]   = useState("idle");      // idle | scanning | ok | error
  const [result,   setResult]   = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [hpField,  setHpField]  = useState("");

  const run = async (e) => {
    e?.preventDefault?.();
    setStatus("scanning"); setErrorMsg(""); setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/api/public/qorbom/demo-scan`,
        { repo_url: repoUrl, hp_field: hpField },
        { timeout: 60000 },
      );
      if (data?.ok && data.result) { setResult(data.result); setStatus("ok"); }
      else throw new Error("Empty response");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Scan failed";
      setErrorMsg(typeof detail === "string" ? detail : "Scan failed");
      setStatus("error");
    }
  };

  return (
    <div className="mt-6" data-testid="qorbom-docs-tryit-section">
      <form
        onSubmit={run}
        className="rounded-md border border-slate-200 bg-slate-50 p-6"
        data-testid="qorbom-docs-tryit-form"
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="url"
            value={repoUrl}
            onChange={(e) => setRepoUrl(e.target.value)}
            required minLength={8} maxLength={400}
            placeholder="https://github.com/OWNER/REPO"
            className="flex-1 rounded-md border border-slate-300 bg-white px-4 py-3 font-mono text-sm text-slate-900 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700"
            data-testid="qorbom-docs-tryit-input"
            disabled={status === "scanning"}
          />
          <button
            type="submit"
            disabled={status === "scanning"}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-800 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-900 disabled:opacity-60"
            data-testid="qorbom-docs-tryit-submit"
          >
            {status === "scanning"
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Scanning…</>
              : <><Play className="h-4 w-4" /> Run scan</>}
          </button>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Rate-limited to 1 scan per IP per 24 hours. Cached for 5 min.
        </p>
        {/* Honeypot — visually hidden from humans */}
        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}>
          <label>Leave blank
            <input type="text" tabIndex={-1} autoComplete="off"
                   value={hpField} onChange={(e) => setHpField(e.target.value)} />
          </label>
        </div>
      </form>

      {status === "error" && (
        <div className="mt-4 flex items-start gap-3 rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900"
             data-testid="qorbom-docs-tryit-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>{errorMsg}</div>
        </div>
      )}

      {status === "ok" && result && <DemoResult result={result} />}
    </div>
  );
}


function DemoResult({ result }) {
  const counts   = result.summary?.counts || {};
  const score    = result.summary?.score;
  const findings = result.findings_preview || [];

  const scoreColor = (s) => {
    if (s === null || s === undefined) return "text-slate-400";
    if (s >= 80) return "text-emerald-600";
    if (s >= 50) return "text-amber-600";
    return "text-rose-600";
  };
  const classBadge = (cls) => ({
    quantum_safe:       "bg-emerald-100 text-emerald-800",
    acceptable:         "bg-slate-100   text-slate-700",
    weak:               "bg-amber-100   text-amber-800",
    quantum_vulnerable: "bg-rose-100    text-rose-800",
  }[cls] || "bg-slate-100 text-slate-600");

  return (
    <div className="mt-6 space-y-4" data-testid="qorbom-docs-tryit-result">
      {result.cached && (
        <div className="rounded-md border-l-4 border-blue-700 bg-blue-50 px-4 py-2 text-xs text-blue-900">
          Cached response (5-min TTL).
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-md border border-slate-200 bg-white p-4 text-center">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Readiness score
          </div>
          <div className={"mt-1 text-4xl font-bold " + scoreColor(score)}
               style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>
            {score === null || score === undefined ? "—" : score}
          </div>
          <div className="text-[10px] text-slate-400">/ 100</div>
        </div>
        <MiniStat label="Files scanned"   value={result.files_scanned ?? 0} />
        <MiniStat label="Crypto findings" value={result.findings_total ?? 0} />
        <MiniStat label="Components"      value={result.components_total ?? 0} />
      </div>

      {Object.keys(counts).length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-4">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Primitive classification
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(counts).map(([cls, n]) => (
              <span key={cls} className={"rounded-md px-3 py-1 text-xs font-medium " + classBadge(cls)}>
                {cls.replace(/_/g, " ")}: <strong>{n}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {findings.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Findings preview
              {result.findings_truncated && (
                <span className="ml-2 text-slate-400">
                  · showing first {findings.length} of {result.findings_total}
                </span>
              )}
            </div>
          </div>
          <ul className="divide-y divide-slate-200" data-testid="qorbom-docs-tryit-findings">
            {findings.map((f, i) => (
              <li key={i} className="flex items-start gap-3 px-4 py-2 text-sm">
                <span className={"mt-0.5 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider " + classBadge(f.classification)}>
                  {f.primitive}
                </span>
                <div className="min-w-0 flex-1">
                  <code className="break-all text-xs text-slate-700"
                        style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                    {f.file}:{f.line}
                  </code>
                  <div className="text-xs text-slate-500">{f.description}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700">
          Like what you see?
        </div>
        <p className="mt-1 text-sm text-blue-900">
          The full <QorbomBrand /> Partner API returns a complete CycloneDX 1.6
          + SPDX 3.0.1 BOM, signed by our methodology, audit-ready.
        </p>
        <Link
          to="/qorbom/become-a-partner"
          className="mt-2 inline-flex items-center gap-2 rounded-md bg-blue-800 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-900"
          data-testid="qorbom-docs-tryit-apply-cta"
        >
          Apply for a partner key <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}


function MiniStat({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 text-center">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-slate-900"
           style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>
        {value}
      </div>
    </div>
  );
}
